// AI-CONTEXT: See docs/features/msi-rekenregels-monetarisering.md
//
// De vier rekenregels van de Meetstandaard Social Impact, toegepast op een
// bruto verschil (euro per jaar). Bron: MSI-kennisbank
// gedeeld/wiki/methodiek/rekenregels-monetarisering.md (matrix §2, volgorde
// 'De berekening in zes stappen'); dezelfde waarden staan in het
// `rekenregels`-blok van de pinned standaarden, en de test legt ze daartegen.
//
// Spiegel: functions/msiMonetisationHelpers/applyRekenregels.js, bewaakt door
// applyRekenregels.parity.test.js.

/** Deel van het gemeten effect dat aan de interventie wordt toegeschreven (bandbreedte 0,30–0,50). */
const IMPACT_CLAIM = 0.4
/** Jaarlijkse afname van het resterende effect, vanaf jaar 2. */
const DROP_OFF = 0.25
/** Reële discontovoet (Werkgroep discontovoet 2025), vanaf jaar 2. */
const DISCOUNT_RATE = 0.028

// Matrix duur × intensiteit: elke duurklasse een jaar extra, elke urenklasse
// een half jaar. Grenzen horen bij de lagere klasse ("t/m").
const MONTH_LIMITS = [3, 6, 12]
const HOUR_LIMITS = [4, 16]

const isDose = (value) => typeof value === 'number' && Number.isFinite(value) && value >= 0

const classIndex = (value, limits) => {
    const index = limits.findIndex((limit) => value <= limit)
    return index === -1 ? limits.length : index
}

/**
 * Tijdshorizon in jaren (1 t/m 5, halve jaren) uit duur en intensiteit van de deelname.
 * @param {{ months?: number|null, hoursPerWeek?: number|null }} [dose]
 * @returns {number|null} null als duur of uren onbekend of ongeldig zijn
 */
const horizonFromDose = ({ months, hoursPerWeek } = {}) => {
    if (!isDose(months) || !isDose(hoursPerWeek)) return null
    return 1 + classIndex(months, MONTH_LIMITS) + 0.5 * classIndex(hoursPerWeek, HOUR_LIMITS)
}

/**
 * Cumulatieve factor Σ (1 − drop-off)^(t−1) / (1 + disconto)^(t−1); jaar 1 telt
 * volledig, een half laatste jaar voor de helft.
 * @param {number} horizon - Tijdshorizon in jaren
 * @returns {number}
 */
const cumulativeFactor = (horizon) => {
    const yearWeight = (t) => ((1 - DROP_OFF) / (1 + DISCOUNT_RATE)) ** (t - 1)
    const fullYears = Math.floor(horizon)
    let factor = 0
    for (let t = 1; t <= fullYears; t += 1) factor += yearWeight(t)
    const rest = horizon - fullYears
    if (rest > 0) factor += rest * yearWeight(fullYears + 1)
    return factor
}

/**
 * Toegerekende meerjarige waarde = verschil × impactclaim × cumulatieve factor.
 * @param {number} delta - Bruto verschil in euro per jaar (mag negatief zijn)
 * @param {number|null|undefined} horizon - Tijdshorizon in jaren, uit horizonFromDose
 * @returns {number|null} null zonder horizon — 'niet doorgerekend', nooit 0
 */
const attributedValue = (delta, horizon) => {
    if (typeof horizon !== 'number' || !Number.isFinite(horizon)) return null
    return delta * IMPACT_CLAIM * cumulativeFactor(horizon)
}

export default attributedValue
export { IMPACT_CLAIM, DROP_OFF, DISCOUNT_RATE, horizonFromDose, cumulativeFactor }
